'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

interface Props {
  /** The server-rendered first page of tiles. */
  children: ReactNode;
  /** Page number the server rendered as `children` (1 unless deep-linked). */
  initialPage: number;
  /** Whether more pages exist beyond `children`. */
  initialHasMore: boolean;
  /**
   * Loads one more page and returns its server-rendered tiles (an RSC payload)
   * plus whether further pages remain. Bound to the collection + active
   * search/filter on the server, so the client passes only the page number.
   */
  loadMoreAction: (
    page: number,
  ) => Promise<{ node: ReactNode; hasMore: boolean }>;
}

/**
 * Progressive-enhancement infinite scroll over a server-rendered first page.
 *
 * The initial tiles arrive as server-rendered `children` (crawlable, in the HTML
 * with no JS). As the sentinel nears the viewport, each subsequent page is
 * fetched through a server action that returns MORE server-rendered tiles, which
 * are appended in place — so even lazily-loaded content is server-rendered, not
 * client-shaped. The real `<a href="?page=N">` is the crawler + no-JS fallback:
 * search engines follow it to a fully server-rendered page, while JS intercepts
 * it to append seamlessly. The grid is pure CSS (`grid-cols-3`), no measurement.
 */
export function InfiniteCollectionProducts({
  children,
  initialPage,
  initialHasMore,
  loadMoreAction,
}: Props) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [batches, setBatches] = useState<ReactNode[]>([]);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [pending, setPending] = useState(false);
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadNext = async () => {
    if (loadingRef.current || !hasMore) {
      return;
    }
    loadingRef.current = true;
    setPending(true);
    try {
      const nextPage = page + 1;
      const result = await loadMoreAction(nextPage);
      setBatches(previous => [...previous, result.node]);
      setPage(nextPage);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Failed to load more collection products:', error);
    } finally {
      loadingRef.current = false;
      setPending(false);
    }
  };

  // Keep the observer (subscribed once) pointed at the freshest handler so it
  // reads the current page without churning a subscription on every render.
  const loadNextRef = useRef(loadNext);
  loadNextRef.current = loadNext;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          void loadNextRef.current();
        }
      },
      { rootMargin: '600px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const nextPageHref = (() => {
    const params = new URLSearchParams(searchParams?.toString() ?? '');
    params.set('page', String(page + 1));
    return `${pathname}?${params.toString()}`;
  })();

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {children}
        {batches}
      </div>
      {hasMore ? (
        <div ref={sentinelRef} className="flex justify-center py-5">
          <a
            href={nextPageHref}
            onClick={event => {
              event.preventDefault();
              void loadNext();
            }}
            className="border-raise/20 text-raise/80 hover:bg-raise/5 text-label rounded-full border px-5 py-2 font-medium transition"
          >
            {pending ? 'Loading…' : 'Load more'}
          </a>
        </div>
      ) : null}
    </>
  );
}
